import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Grid,
  Typography,
  Pagination,
  Stack,
  TextField,
  MenuItem,
} from "@mui/material";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import ReminderLogCard from "../components/reminderLogs/ReminderLogCard";
import AppCircularProgress from "../components/common/AppCircularProgress";
import DateRangeFilter from "../components/common/DateRangeFilter";

import useDebounce from "../hooks/useDebounce";
import { getReminderLogs } from "../redux/actions/reminderLogActions";
import { castEnumForDisplay } from "../helpers/enumHelpers";
import PageError from "../components/common/PageError";

const ReminderLogsPage = () => {
  const dispatch = useDispatch();
  const today = new Date().toISOString().split("T")[0];

  const {
    reminderLogs,
    loadingReminderLogs,
    errorLoadingReminderLogs,
    pagination,
  } = useSelector((state) => state.reminderLog);

  // --- Local State for Filters ---
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);

  const [filters, setFilters] = useState({
    type: "",
    startDate: today,
    endDate: today,
  });

  const [page, setPage] = useState(1);
  const limit = 12;

  // --- Reminder Types from your Enum ---
  const reminderTypes = [
    "EXPIRY_30_DAY",
    "EXPIRY_14_DAY",
    "EXPIRY_7_DAY",
    "EXPIRY_1_DAY",
    "EXPIRED_NOTICE",
  ];

  const fetchLogs = useCallback(() => {
    dispatch(
      getReminderLogs({
        page,
        size: limit,
        search: debouncedSearch,
        type: filters.type,
        startDate: filters.startDate,
        endDate: filters.endDate,
      }),
    );
  }, [dispatch, page, debouncedSearch, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const renderContent = () => {
    if (loadingReminderLogs) return <AppCircularProgress />;

    if (errorLoadingReminderLogs)
      return <PageError error={errorLoadingReminderLogs} onClick={fetchLogs} />;

    if (!reminderLogs || reminderLogs.length === 0)
      return (
        <Box textAlign="center" mt={5}>
          <Typography color="text.secondary">
            No reminder logs found matching your criteria.
          </Typography>
        </Box>
      );

    return (
      <>
        <Grid container spacing={3}>
          {reminderLogs.map((log) => (
            <Grid item key={log.id} xs={12} sm={6} md={4} lg={3}>
              <ReminderLogCard log={log} />
            </Grid>
          ))}
        </Grid>

        {pagination?.totalPages > 1 && (
          <Stack alignItems="center" mt={4}>
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={(e, v) => setPage(v)}
              color="primary"
            />
          </Stack>
        )}
      </>
    );
  };

  return (
    <PageContainer
      title="Reminder History"
      subtitle="Audit log of all automated customer notifications and license expiry alerts."
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search by business name or key..."
      >
        {/* Type Dropdown */}
        <TextField
          select
          label="Reminder Type"
          size="small"
          name="type"
          value={filters.type}
          onChange={handleFilterChange}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All Types</MenuItem>
          {reminderTypes.map((type) => (
            <MenuItem key={type} value={type}>
              {castEnumForDisplay(type)}
            </MenuItem>
          ))}
        </TextField>

        <DateRangeFilter
          startDate={filters.startDate}
          setStartDate={(newDate) =>
            setFilters((prev) => ({ ...prev, startDate: newDate }))
          }
          endDate={filters.endDate}
          setEndDate={(newDate) =>
            setFilters((prev) => ({ ...prev, endDate: newDate }))
          }
        />
      </PageToolbar>

      {renderContent()}
    </PageContainer>
  );
};

export default ReminderLogsPage;
